import type { Result } from '@/stores/type/result.type'
import type { LifecycleTotals, ResultWithPhases } from '@/views/calculations/type/resultView.type'
import { DEFAULT_SELECTED_PHASES, LIFECYCLE_PHASE_GROUPS } from '@/constants/chartConfiguration'
import { RESULT_PHASE_DEFINITIONS } from '@/constants/lifecyclePhases'
import { formatMoney, toAmount } from '@/views/calculations/utils/resultChart/format'

const getLifecycleTotals = (result: ResultWithPhases): LifecycleTotals => {
  return {
    production: toAmount(result.production),
    building: toAmount(result.building),
    use: toAmount(result.use_maintenance),
    demolition:
      toAmount(result.C1) + toAmount(result.C2) + toAmount(result.C3) + toAmount(result.C4),
    endOfLife: toAmount(result.D)
  }
}

export const phaseGroupValue = (result: ResultWithPhases, group: string): number => {
  switch (group) {
    case LIFECYCLE_PHASE_GROUPS.PRODUCTION:
      return toAmount(result.production)
    case LIFECYCLE_PHASE_GROUPS.BUILDING_A4:
      return toAmount(result.A4)
    case LIFECYCLE_PHASE_GROUPS.BUILDING_A5:
      return toAmount(result.A5)
    case LIFECYCLE_PHASE_GROUPS.USE_MAINTENANCE:
      return toAmount(result.use_maintenance)
    case LIFECYCLE_PHASE_GROUPS.DEMOLITION_C1:
      return toAmount(result.C1)
    case LIFECYCLE_PHASE_GROUPS.DEMOLITION_C2:
      return toAmount(result.C2)
    case LIFECYCLE_PHASE_GROUPS.DEMOLITION_C3_C4:
      return toAmount(result.C3) + toAmount(result.C4)
    case LIFECYCLE_PHASE_GROUPS.END_OF_LIFE:
      return toAmount(result.D)
    default:
      return 0
  }
}

export const sumSelectedPhases = (result: Result, selectedPhases: string[]): number => {
  const groups = selectedPhases.length ? selectedPhases : DEFAULT_SELECTED_PHASES
  let sum = 0

  for (const group of groups) {
    sum += phaseGroupValue(result as ResultWithPhases, group)
  }

  return sum
}

export const buildTooltipLines = (result?: ResultWithPhases) => {
  if (!result) {
    return []
  }

  const lines: string[] = []

  lines.push('')
  lines.push('Phase Breakdown:')

  RESULT_PHASE_DEFINITIONS.forEach((phase) => {
    const phaseValue = toAmount(result[phase.key])

    if (phaseValue !== 0) {
      lines.push(`  ${phase.label}: ${formatMoney(phaseValue)}`)
    }
  })

  lines.push('')
  lines.push('Lifecycle Stages:')

  const lifecycle = getLifecycleTotals(result)
  lines.push(`  Production (A1-A3): ${formatMoney(lifecycle.production)}`)
  lines.push(`  Building (A4-A5): ${formatMoney(lifecycle.building)}`)
  lines.push(`  Use (B): ${formatMoney(lifecycle.use)}`)
  lines.push(`  Demolition (C): ${formatMoney(lifecycle.demolition)}`)
  lines.push(`  End-of-Life (D): ${formatMoney(lifecycle.endOfLife)}`)

  return lines
}
